<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { useForm } from '@inertiajs/vue3';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import Card from 'primevue/card';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import RadioButton from 'primevue/radiobutton';
import Tag from 'primevue/tag';
import MainLayout from '@/layout/main/MainLayout.vue';

defineOptions({
    layout: MainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { formatCents } = useCurrencyFormatter();

const props = defineProps({
    site: Object,
    coins: Object,
    note: Object,
});

// Tab management
const activeIndex = ref(0);

// Form for note
const noteForm = useForm({
    text: props.note?.text ?? '',
});

// Options
const linkTypeOptions = [
    { label: 'DOFOLLOW', value: 'DOFOLLOW' },
    { label: 'NOFOLLOW', value: 'NOFOLLOW' },
];

const linksOptions = computed(() => [
    { label: '3', value: 3 },
    { label: '5', value: 5 },
    { label: $t('No limit'), value: null },
]);

const imagesOptions = [
    { label: '1', value: 1 },
    { label: '3', value: 3 },
    { label: '5', value: 5 },
];

// Computed properties
const saleFormat = computed(() => props.coins[props.site.sale_coin]);

// Methods
const submitNote = () => {
    noteForm.put(route('client.notes.update', props.site.id), {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Note updated successfully',
                life: 3000
            });
        }
    });
};

const formatCurrency = (value, coin) => {
    if (!value || !coin) return '';
    return formatCents(value, coin);
};

const getStatusSeverity = (status) => {
    switch (status) {
        case 'PENDING': return 'warn';
        case 'ENABLED': return 'success';
        case 'DISABLED': return 'danger';
        default: return 'info';
    }
};
</script>

<template>
    <!-- Site Header -->
    <div class="grid gap-6 mb-6">
        <div class="col-12">
            <Card>
                <template #title>
                    <div class="flex align-items-center justify-content-between">
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-globe text-2xl"></i>
                            <span class="self-center">{{ $t('Show site') }}: {{ site.real_url || site.url }}</span>
                            <Tag
                                v-if="site.formatted_status"
                                :value="site.formatted_status"
                                :severity="getStatusSeverity(site.status)"
                            />
                        </div>
                    </div>
                </template>
            </Card>
        </div>
    </div>

    <!-- Tab View -->
    <TabView v-model:activeIndex="activeIndex">
        <!-- Site Info Tab -->
        <TabPanel>
            <template #header>
                <i class="pi pi-info-circle mr-2"></i>
                {{ $t('Site Information') }}
            </template>

            <div class="grid gap-6">
                <!-- Note Section -->
                <div class="col-12">
                    <Card>
                        <template #title>
                            <div class="flex align-items-center gap-2">
                                <i class="pi pi-file-edit"></i>
                                <span>{{ $t('Edit note') }}</span>
                            </div>
                        </template>
                        <template #content>
                            <form @submit.prevent="submitNote">
                                <div class="field">
                                    <label for="note" class="font-semibold">{{ $t('Note') }}</label>
                                    <Textarea
                                        id="note"
                                        v-model="noteForm.text"
                                        rows="3"
                                        class="w-full"
                                        :placeholder="$t('Add a note about this site')" />
                                </div>
                                <Button type="submit"
                                        :label="$t('Save Note')"
                                        icon="pi pi-save"
                                        :loading="noteForm.processing" />
                            </form>
                        </template>
                    </Card>
                </div>

                <!-- Site Details -->
                <div class="col-12">
                    <Card>
                        <template #title>
                            <div class="flex align-items-center gap-2">
                                <i class="pi pi-cog"></i>
                                <span>{{ $t('Site Details') }}</span>
                            </div>
                        </template>
                        <template #content>
                            <div class="formgrid grid">
                                <!-- URL -->
                                <div class="field col-12">
                                    <label class="font-semibold">{{ $t('URL') }}</label>
                                    <InputText
                                        :value="site.real_url || site.url"
                                        class="w-full"
                                        disabled />
                                </div>

                                <!-- Category -->
                                <div class="field col-12">
                                    <label class="font-semibold">{{ $t('Category') }}</label>
                                    <InputText
                                        :value="site.category?.name"
                                        class="w-full"
                                        disabled />
                                </div>

                                <!-- Metrics -->
                                <div class="field col-12 md:col-4">
                                    <label class="font-semibold">{{ $t('DA') }}</label>
                                    <InputNumber
                                        :modelValue="site.da"
                                        class="w-full"
                                        disabled />
                                </div>

                                <div class="field col-12 md:col-4">
                                    <label class="font-semibold">{{ $t('DR') }}</label>
                                    <InputNumber
                                        :modelValue="site.dr"
                                        class="w-full"
                                        disabled />
                                </div>

                                <div class="field col-12 md:col-4">
                                    <label class="font-semibold">{{ $t('Traffic') }}</label>
                                    <InputNumber
                                        :modelValue="site.traffic"
                                        class="w-full"
                                        disabled />
                                </div>

                                <!-- Language and Country -->
                                <div class="field col-12 md:col-6">
                                    <label class="font-semibold">{{ $t('Language') }}</label>
                                    <InputText
                                        :value="site.language?.name"
                                        class="w-full"
                                        disabled />
                                </div>

                                <div class="field col-12 md:col-6">
                                    <label class="font-semibold">{{ $t('Country') }}</label>
                                    <InputText
                                        :value="site.country?.name"
                                        class="w-full"
                                        disabled />
                                </div>

                                <!-- Link Type -->
                                <div class="field col-12">
                                    <label class="font-semibold">{{ $t('Link Type') }}</label>
                                    <Select
                                        :modelValue="site.link_type"
                                        :options="linkTypeOptions"
                                        optionLabel="label"
                                        optionValue="value"
                                        class="w-full"
                                        disabled />
                                </div>

                                <!-- Features -->
                                <div class="field col-12">
                                    <label class="font-semibold">{{ $t('Features') }}</label>
                                    <div class="flex flex-wrap gap-3 mt-2">
                                        <div class="flex align-items-center">
                                            <Checkbox :modelValue="site.sponsor" :binary="true" disabled />
                                            <label class="ml-2">{{ $t('Sponsor') }}</label>
                                        </div>
                                        <div class="flex align-items-center">
                                            <Checkbox :modelValue="site.menu" :binary="true" disabled />
                                            <label class="ml-2">{{ $t('Menu') }}</label>
                                        </div>
                                        <div class="flex align-items-center">
                                            <Checkbox :modelValue="site.banner" :binary="true" disabled />
                                            <label class="ml-2">{{ $t('Banner') }}</label>
                                        </div>
                                        <div class="flex align-items-center">
                                            <Checkbox :modelValue="site.promo" :binary="true" disabled />
                                            <label class="ml-2">{{ $t('Promo') }}</label>
                                        </div>
                                        <div class="flex align-items-center">
                                            <Checkbox :modelValue="site.google_news" :binary="true" disabled />
                                            <label class="ml-2">{{ $t('Google News') }}</label>
                                        </div>
                                        <div class="flex align-items-center">
                                            <Checkbox :modelValue="site.embed" :binary="true" disabled />
                                            <label class="ml-2">{{ $t('Embed') }}</label>
                                        </div>
                                    </div>
                                </div>

                                <!-- Restrictions -->
                                <div class="field col-12" v-if="site.restrict">
                                    <div class="flex align-items-center mb-2">
                                        <Checkbox :modelValue="site.restrict" :binary="true" disabled />
                                        <label class="ml-2 font-semibold">{{ $t('Restriction') }}</label>
                                    </div>
                                    <InputText
                                        v-if="site.restrict_detail"
                                        :value="site.restrict_detail"
                                        class="w-full"
                                        disabled />
                                </div>

                                <!-- Archive Article -->
                                <div class="field col-12" v-if="site.archive_article">
                                    <div class="flex align-items-center mb-2">
                                        <Checkbox :modelValue="site.archive_article" :binary="true" disabled />
                                        <label class="ml-2 font-semibold">{{ $t('Archive article') }}</label>
                                    </div>
                                    <InputText
                                        v-if="site.archive_due"
                                        :value="site.archive_due"
                                        class="w-full"
                                        disabled />
                                </div>

                                <!-- Links and Images -->
                                <div class="field col-12 md:col-6">
                                    <label class="font-semibold">{{ $t('Number of links in the article') }}</label>
                                    <div class="flex gap-3 mt-2">
                                        <div v-for="option in linksOptions" :key="option.value" class="flex align-items-center">
                                            <RadioButton :modelValue="site.links === option.value" :value="true" disabled />
                                            <label class="ml-2">{{ option.label }}</label>
                                        </div>
                                    </div>
                                </div>

                                <div class="field col-12 md:col-6">
                                    <label class="font-semibold">{{ $t('Maximum number of images in content') }}</label>
                                    <div class="flex gap-3 mt-2">
                                        <div v-for="option in imagesOptions" :key="option.value" class="flex align-items-center">
                                            <RadioButton :modelValue="site.images === option.value" :value="true" disabled />
                                            <label class="ml-2">{{ option.label }}</label>
                                        </div>
                                    </div>
                                </div>

                                <!-- Last Posted -->
                                <div class="field col-12">
                                    <label class="font-semibold">{{ $t('Last Posted') }}</label>
                                    <InputText
                                        :value="site.last_posted"
                                        type="date"
                                        class="w-full"
                                        disabled />
                                </div>

                                <!-- Observations -->
                                <div class="field col-12">
                                    <label class="font-semibold">{{ $t('Observations') }}</label>
                                    <Textarea
                                        :value="site.obs"
                                        rows="3"
                                        class="w-full"
                                        disabled />
                                </div>

                                <!-- Rules -->
                                <div class="field col-12">
                                    <label class="font-semibold">{{ $t('Rules for content') }}</label>
                                    <Textarea
                                        :value="site.rules"
                                        rows="3"
                                        class="w-full"
                                        disabled />
                                </div>

                                <!-- Example Article -->
                                <div class="field col-12">
                                    <label class="font-semibold">{{ $t('Example article') }}</label>
                                    <InputText
                                        :value="site.example_article"
                                        class="w-full"
                                        disabled />
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>
            </div>
        </TabPanel>

        <!-- Pricing Tab -->
        <TabPanel>
            <template #header>
                <i class="pi pi-dollar mr-2"></i>
                {{ $t('Pricing') }}
            </template>

            <Card>
                <template #title>
                    <div class="flex align-items-center gap-2">
                        <i class="pi pi-tag"></i>
                        <span>{{ $t('Pricing table') }}</span>
                    </div>
                </template>
                <template #content>
                    <DataTable :value="[{ name: $t('Default'), sale: site.sale, sale_coin: site.sale_coin }, ...(site.types || [])]" class="p-datatable-sm">
                        <Column :header="$t('Type')" style="width: 50%">
                            <template #body="slotProps">
                                <span class="font-semibold">
                                    {{ slotProps.data.name }}
                                </span>
                            </template>
                        </Column>
                        <Column :header="$t('Sale')" style="width: 50%">
                            <template #body="slotProps">
                                <span>
                                    {{ slotProps.data.pivot ? formatCurrency(slotProps.data.pivot.sale, slotProps.data.pivot.sale_coin) : formatCurrency(slotProps.data.sale, slotProps.data.sale_coin) || 'R$ 0,00'}}
                                </span>
                            </template>
                        </Column>
                    </DataTable>
                </template>
            </Card>
        </TabPanel>
    </TabView>
</template>

<style scoped>
.field {
    margin-bottom: 1.5rem;
}

.field label {
    display: block;
    margin-bottom: 0.5rem;
}
</style>
